import React, { useEffect, useRef, useState, useMemo } from "react";
import { api } from "../api";
import MainLayout from "../components/mainLayout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Eye,
  EyeOff,
  Settings,
  X,
  Check,
  Trash2,
  Edit3,
  Plus,
  Copy,
  ShieldCheck,
  KeyRound,
  RefreshCcw,
  AlertTriangle,
} from "lucide-react";
import { showInfo, showError, showSuccess } from "../utils/toast";
import SelectFuncionario from "../components/ui/selectFuncionario";
import "../sass/pages/cadastroFuncScreen.scss";

/* ================= Debug ================= */
const DEBUG = true;
const dlog = (...args) => DEBUG && console.log("[FUNC]", ...args);

/* ================= Helpers ================= */
const digits = (v = "") => (v || "").replace(/\D+/g, "");
const upper2 = (v = "") => (v || "").toUpperCase().slice(0, 2);
const normalizaSN = (v, padrao = "S") => {
  const valor = String(v ?? padrao).trim().toUpperCase();
  return valor === "N" ? "N" : "S";
};
const normalizaBloqueado = (v) => {
  const s = String(v ?? "0").trim().toUpperCase();
  return s === "1" || s === "TRUE" || s === "S" ? 1 : 0;
};

const loginFrom = (nome = "", email = "") => {
  const base =
    (nome || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ".")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") || (email || "").split("@")[0];

  return (base || "").replace(/[^a-z0-9.]/g, "").slice(0, 30);
};

const maskCEP = (v = "") => {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, "$1-$2");
};

const norm = (s = {}) => ({
  id: s.ID ?? s.Id ?? s.IdSetor ?? s.id ?? null,
  nome: s.Setor ?? s.Nome ?? s.NomeSetor ?? s.nome ?? "",
});

const eqi = (a = "", b = "") =>
  String(a)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase() ===
  String(b)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/* valores compatíveis com backend */
const NIVEL_OPTS = [
  { value: "ADM", label: "ADMINISTRADOR" },
  { value: "SUP", label: "SUPERVISOR" },
  { value: "GER", label: "GERENTE" },
  { value: "TI", label: "TI" },
  { value: "USER", label: "USUÁRIO" },
];

const SITUACAO_OPTS = ["Ativo", "Inativo", "Férias", "Afastado"];

const NIVEL_PARAMETRO_OPTS = [
  { value: 0, label: "N0 · Sem permissão", desc: "Não pode alterar parâmetros governados." },
  { value: 1, label: "N1 · Operacional básico", desc: "Pode alterar parâmetros com NIVEL_MUTACAO até 1." },
  { value: 2, label: "N2 · Operacional controlado", desc: "Pode alterar parâmetros com NIVEL_MUTACAO até 2." },
  { value: 3, label: "N3 · Sensível", desc: "Pode alterar parâmetros com NIVEL_MUTACAO até 3." },
  { value: 4, label: "N4 · Crítico", desc: "Pode alterar parâmetros com NIVEL_MUTACAO até 4." },
  { value: 5, label: "N5 · Máximo", desc: "Pode alterar parâmetros com NIVEL_MUTACAO até 5." },
];

const normalizarNivelParametro = (valor) => {
  const nivel = Number(valor);
  return Number.isInteger(nivel) && nivel >= 0 && nivel <= 5 ? nivel : 0;
};

/* ================= Inline Drawer (Setores) ================= */
function InlineSetoresDrawer({ open, onClose, itens, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [editingNome, setEditingNome] = useState("");
  const [newNome, setNewNome] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      dlog("Drawer Setores aberto");
      setEditingId(null);
      setEditingNome("");
      setNewNome("");
      setDeletingId(null);
    }
  }, [open]);

  const toPayload = (nome) => ({ Nome: nome, Setor: nome, Descricao: nome });

  const createSetor = async () => {
    const nome = String(newNome || "").trim();
    if (!nome) return;

    setLoading(true);

    try {
      await api.post("/setores", toPayload(nome));
      showSuccess("Setor criado.");
      setNewNome("");
      onRefresh?.();
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao criar setor.");
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    const nome = String(editingNome || "").trim();
    if (!editingId || !nome) return;

    setLoading(true);

    try {
      await api.put(`/setores/${editingId}`, toPayload(nome));
      showSuccess("Setor atualizado.");
      setEditingId(null);
      setEditingNome("");
      onRefresh?.();
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao atualizar setor.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) =>
    setDeletingId((prev) => (prev === id ? null : id));

  const doDelete = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      await api.delete(`/setores/${id}`);
      showSuccess("Setor excluído.");
      setDeletingId(null);
      onRefresh?.();
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao excluir setor.");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      display: open ? "block" : "none",
      zIndex: 10990,
    },
    drawer: {
      position: "fixed",
      top: 0,
      right: 0,
      height: "100vh",
      width: "min(560px, 92vw)",
      background: "#fff",
      boxShadow: "-8px 0 24px rgba(0,0,0,0.2)",
      transform: open ? "translateX(0)" : "translateX(100%)",
      transition: "transform 200ms ease",
      zIndex: 11000,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      padding: "16px 16px",
      borderBottom: "1px solid #eee",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    body: {
      padding: 16,
      overflow: "auto",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    row: {
      display: "grid",
      gridTemplateColumns: "1fr auto auto",
      gap: 8,
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px dashed #eee",
    },
    actions: { display: "inline-flex", gap: 6 },
    input: {
      width: "100%",
      padding: "8px 10px",
      border: "1px solid #dcdcdc",
      borderRadius: 6,
      outline: "none",
    },
    toolbar: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 8,
      marginBottom: 8,
    },
    chipWarn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 8px",
      background: "#fff7e6",
      color: "#8a6100",
      border: "1px solid #ffe3af",
      borderRadius: 6,
      fontSize: 12,
    },
    btn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 6,
      border: "1px solid #dcdcdc",
      background: "#fff",
      cursor: "pointer",
    },
    btnPrimary: {
      background: "#0f62fe",
      color: "#fff",
      border: "1px solid #0f62fe",
    },
    btnDanger: {
      color: "#b42318",
      border: "1px solid #f3b0aa",
      background: "#fff",
    },
    btnGhost: { border: "1px solid transparent", background: "transparent" },
  };

  return (
    <>
      <div
        style={styles.overlay}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
      />

      <aside style={styles.drawer} role="dialog" aria-label="Gerenciar Setores">
        <div style={styles.header}>
          <strong>Gerenciar Setores</strong>

          <button
            type="button"
            style={{ ...styles.btn, ...styles.btnGhost }}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            aria-label="Fechar"
            title="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.toolbar}>
            <input
              style={styles.input}
              placeholder="Novo setor..."
              value={newNome}
              onChange={(e) => setNewNome(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createSetor();
              }}
            />

            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={createSetor}
              disabled={!String(newNome || "").trim() || loading}
              title="Adicionar setor"
            >
              <Plus size={16} /> Adicionar
            </button>
          </div>

          {!itens?.length && (
            <div style={styles.chipWarn}>Nenhum setor cadastrado.</div>
          )}

          {itens?.map((s) => {
            const isEditing = editingId === s.id;
            const isDeleting = deletingId === s.id;

            return (
              <div key={s.id} style={styles.row}>
                {isEditing ? (
                  <input
                    style={styles.input}
                    value={editingNome}
                    onChange={(e) => setEditingNome(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditingNome("");
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <div>{s.nome}</div>
                )}

                <div style={styles.actions}>
                  {!isEditing ? (
                    <button
                      type="button"
                      style={styles.btn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(s.id);
                        setEditingNome(s.nome);
                        setDeletingId(null);
                      }}
                      title="Editar"
                    >
                      <Edit3 size={16} /> Editar
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnPrimary }}
                        onClick={(e) => {
                          e.stopPropagation();
                          saveEdit();
                        }}
                        disabled={!String(editingNome || "").trim() || loading}
                        title="Salvar"
                      >
                        <Check size={16} /> Salvar
                      </button>

                      <button
                        type="button"
                        style={styles.btn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(null);
                          setEditingNome("");
                        }}
                        title="Cancelar"
                      >
                        <X size={16} /> Cancelar
                      </button>
                    </>
                  )}
                </div>

                <div style={styles.actions}>
                  {isDeleting ? (
                    <>
                      <button
                        type="button"
                        style={{ ...styles.btn, ...styles.btnDanger }}
                        onClick={(e) => {
                          e.stopPropagation();
                          doDelete(s.id);
                        }}
                        disabled={loading}
                        title="Confirmar exclusão"
                      >
                        <Trash2 size={16} /> Confirmar
                      </button>

                      <button
                        type="button"
                        style={styles.btn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingId(null);
                        }}
                        title="Cancelar"
                      >
                        <X size={16} /> Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      style={styles.btn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(null);
                        setEditingNome("");
                        confirmDelete(s.id);
                      }}
                      title="Excluir"
                    >
                      <Trash2 size={16} /> Excluir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}

/* ================= Tela de Funcionário ================= */
const DEFAULT = {
  id: "",
  codFunc: "",
  seuCodigo: "",
  nome: "",
  usuario: "",
  cpf: "",
  email: "",
  telefone: "",
  celular: "",
  cep: "",
  rua: "",
  numero: "",
  bairro: "",
  cidade: "",
  estado: "",
  cargo: "",
  setorId: "",
  nivel: "",
  nivelParametro: 0,
  situacao: "Ativo",
  senha: "",
  confirmarSenha: "",
  codFuncNew: "",
  codCad: "",
  bloqueado: 0,
  dmControlaPonto: "S",
  dmExibeLembretePonto: "S",
};

const DEFAULT_HOR = {
  NomeFunc: "",
  EntradaManha: "",
  SaidaManha: "",
  EntradaTarde: "",
  SaidaTarde: "",
  TempoAlmoco: "",
  Tolerancia: "",
};

const MODULO_AGENDA = "AGENDA";

const AGENDA_PERMISSOES = [
  {
    key: "CRIAR",
    boolKey: "podeAgendar",
    label: "Criar agendamentos",
    desc: "Permite gravar novos agendamentos para clientes e técnicos.",
    critical: true,
  },
  {
    key: "EDITAR",
    boolKey: "podeEditar",
    label: "Editar agendamentos",
    desc: "Permite alterar data, horário, cliente, serviço e dados operacionais.",
    critical: true,
  },
  {
    key: "EXCLUIR",
    boolKey: "podeExcluir",
    label: "Excluir agendamentos",
    desc: "Permite remover agendamentos. Use com critério operacional.",
    critical: true,
  },
  {
    key: "CONFIRMAR",
    boolKey: "podeConfirmar",
    label: "Confirmar visitas",
    desc: "Permite marcar confirmação do cliente e informar contato.",
    critical: false,
  },
  {
    key: "FINALIZAR",
    boolKey: "podeFinalizar",
    label: "Finalizar atendimentos",
    desc: "Permite encerrar agendamentos após execução ou atendimento.",
    critical: false,
  },
  {
    key: "VER_TODOS",
    boolKey: "podeVerTodos",
    label: "Ver agenda geral",
    desc: "Permite visualizar agenda de outros técnicos/responsáveis.",
    critical: true,
  },
  {
    key: "RELATORIO",
    boolKey: "podeEmitirRelatorio",
    label: "Emitir relatórios",
    desc: "Permite acessar relatórios e impressões gerenciais da agenda.",
    critical: false,
  },
  {
    key: "GERENCIAR_PERMISSOES",
    boolKey: "podeGerenciarPermissoes",
    label: "Gerenciar permissões",
    desc: "Permite conceder ou revogar permissões de agenda para outros usuários.",
    critical: true,
    danger: true,
  },
];

const DEFAULT_PERMISSOES_AGENDA = AGENDA_PERMISSOES.reduce((acc, p) => {
  acc[p.boolKey] = false;
  return acc;
}, {});

const DEFAULT_PERMISSOES_META = {
  vigenciaInicio: "",
  vigenciaFim: "",
  motivo: "",
  observacao: "",
  escopo: "GLOBAL",
  valorEscopo: "",
  permissoesDetalhe: [],
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;

  const s = String(v ?? "").trim().toUpperCase();
  return ["1", "TRUE", "S", "SIM", "YES"].includes(s);
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fmtDateTimeBR = (value) => {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtVigenciaPermissao = (inicio, fim) => {
  const ini = fmtDateTimeBR(inicio) || "agora";
  const end = fmtDateTimeBR(fim) || "sem vencimento";
  return `${ini} até ${end}`;
};

const getPrimeiroValor = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && String(v).trim?.() !== "");

const extrairPermissoesAgenda = (raw) => {
  const root = raw?.data ?? raw ?? {};
  const data = root?.data ?? root;
  const nested =
    data?.permissoesAgenda ||
    data?.permissoes ||
    root?.permissoesAgenda ||
    root?.permissoes ||
    {};

  const out = { ...DEFAULT_PERMISSOES_AGENDA };

  AGENDA_PERMISSOES.forEach((p) => {
    const valor = getPrimeiroValor(
      data?.[p.boolKey],
      root?.[p.boolKey],
      nested?.[p.boolKey],
      nested?.[p.key],
      data?.[p.key],
      root?.[p.key]
    );

    out[p.boolKey] = toBool(valor);
  });

  return out;
};

const permissaoAgendaParaApi = (permissoes) =>
  AGENDA_PERMISSOES.reduce((acc, p) => {
    acc[p.key] = !!permissoes[p.boolKey];
    return acc;
  }, {});

const perfilPermissoesAgenda = (perfil) => {
  const allFalse = { ...DEFAULT_PERMISSOES_AGENDA };

  const perfis = {
    SEM_ACESSO: allFalse,
    OPERADOR: {
      ...allFalse,
      podeAgendar: true,
      podeEditar: true,
      podeConfirmar: true,
      podeFinalizar: true,
      podeEmitirRelatorio: true,
    },
    RESPONSAVEL: {
      ...allFalse,
      podeAgendar: true,
      podeEditar: true,
      podeExcluir: true,
      podeConfirmar: true,
      podeFinalizar: true,
      podeVerTodos: true,
      podeEmitirRelatorio: true,
    },
    ADMIN_AGENDA: AGENDA_PERMISSOES.reduce((acc, p) => {
      acc[p.boolKey] = true;
      return acc;
    }, {}),
  };

  return perfis[perfil] || allFalse;
};

export default function CadastroFuncScreen() {
  const [activeTab, setActiveTab] = useState("INFO");

  const [form, setForm] = useState(DEFAULT);
  const [selecionado, setSelecionado] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvandoBloqueio, setSalvandoBloqueio] = useState(false);
  const [salvandoNivelParametro, setSalvandoNivelParametro] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);

  const [setores, setSetores] = useState([]);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [openSetores, setOpenSetores] = useState(false);

  const usuarioCustom = useRef(false);

  const [hor, setHor] = useState(DEFAULT_HOR);
  const [loadingHor, setLoadingHor] = useState(false);

  const [senhaAtual, setSenhaAtual] = useState("");

  const [permissoesAgenda, setPermissoesAgenda] = useState(DEFAULT_PERMISSOES_AGENDA);
  const [permissoesMeta, setPermissoesMeta] = useState(DEFAULT_PERMISSOES_META);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);
  const [salvandoPermissoes, setSalvandoPermissoes] = useState(false);
  const [permissoesDirty, setPermissoesDirty] = useState(false);

  const isADM = useMemo(
    () =>
      String(sessionStorage.getItem("NivelFunc") || "")
        .trim()
        .toUpperCase() === "ADM",
    []
  );

  useEffect(() => {
    const tk = sessionStorage.getItem("token") || localStorage.getItem("token");

    if (tk) {
      api.defaults.headers.common.Authorization = `Bearer ${tk}`;
      dlog("Auth token set");
    }
  }, []);

  useEffect(() => {
    if (!usuarioCustom.current) {
      setForm((p) => ({ ...p, usuario: loginFrom(p.nome, p.email) }));
    }
  }, [form.nome, form.email]);

  const fetchSetores = async () => {
    setLoadingSetores(true);

    try {
      const { data } = await api.get("/setores");

      const arr = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

      const items = arr.map(norm).filter((x) => x.id && x.nome);
      setSetores(items);
    } catch {
      showError("Falha ao carregar setores.");
    } finally {
      setLoadingSetores(false);
    }
  };

  useEffect(() => {
    fetchSetores();
  }, []);

  useEffect(() => {
    if (!form.setorId && form.cargo && setores.length) {
      const hit = setores.find((s) => eqi(s.nome, form.cargo));
      if (hit) setForm((p) => ({ ...p, setorId: String(hit.id) }));
    }
  }, [setores, form.cargo, form.setorId]);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === "usuario") usuarioCustom.current = true;
    if (name === "cep") value = maskCEP(value);

    if (name === "setorId") {
      const hit = setores.find((s) => String(s.id) === String(value));
      setForm((prev) => ({ ...prev, setorId: value, cargo: hit?.nome || "" }));
      return;
    }

    if (name === "dmControlaPonto" || name === "dmExibeLembretePonto") {
      if (!isADM) return;
      value = normalizaSN(value);
    }

    if (name === "nivelParametro") {
      if (!isADM) return;
      value = normalizarNivelParametro(value);
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const limpar = () => {
    setSelecionado(null);
    usuarioCustom.current = false;
    setForm(DEFAULT);
    setHor(DEFAULT_HOR);
    setSenhaAtual("");
    setPermissoesAgenda(DEFAULT_PERMISSOES_AGENDA);
    setPermissoesMeta(DEFAULT_PERMISSOES_META);
    setPermissoesDirty(false);
    setActiveTab("INFO");
  };

  const buscarCEP = async () => {
    const cep = digits(form.cep);
    if (cep.length !== 8) return;

    setLoadingCep(true);

    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();

      if (data?.erro) return showError("CEP não encontrado.");

      setForm((p) => ({
        ...p,
        rua: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.localidade || "",
        estado: (data.uf || "").toUpperCase(),
      }));
    } catch {
      showError("Falha ao consultar CEP.");
    } finally {
      setLoadingCep(false);
    }
  };

  const toCreatePayload = () => ({
    Nome: form.nome,
    CPF: digits(form.cpf),
    Email: form.email,
    Telefone: digits(form.telefone),
    Celular: digits(form.celular),
    CEP: digits(form.cep),
    Rua: form.rua,
    Numero: form.numero,
    Bairro: form.bairro,
    Cidade: form.cidade,
    Estado: upper2(form.estado),
    Cargo: form.cargo || "",
    IdSetor: form.setorId ? Number(form.setorId) : null,
    Nivel: form.nivel,
    Situacao: form.situacao || "Ativo",
    Usuario: form.usuario || loginFrom(form.nome, form.email),
    Senha: form.senha,
    DM_CONTROLA_PONTO: isADM ? form.dmControlaPonto : "S",
    DM_EXIBE_LEMBRETE_PONTO: isADM ? form.dmExibeLembretePonto : "S",
    CriadoPor: sessionStorage.getItem("usuario") || "web",
  });

  const toUpdatePayload = () => ({
    Email: form.email,
    Telefone: digits(form.telefone),
    Celular: digits(form.celular),
    CEP: digits(form.cep),
    Rua: form.rua,
    Numero: form.numero,
    Bairro: form.bairro,
    Cidade: form.cidade,
    Estado: upper2(form.estado),
    Cargo: form.cargo || "",
    IdSetor: form.setorId ? Number(form.setorId) : null,
    Nivel: form.nivel,
    Situacao: form.situacao,
    DM_CONTROLA_PONTO: form.dmControlaPonto,
    DM_EXIBE_LEMBRETE_PONTO: form.dmExibeLembretePonto,
    AtualizadoPor: sessionStorage.getItem("usuario") || "web",
  });

  const fromApiToForm = (f) => {
    const mapped = {
      id: f.IdFuncionario || "",
      codFunc: f.CodFunc != null ? String(f.CodFunc) : "",
      seuCodigo: f.CodFunc_New || "",
      nome: f.Nome || "",
      usuario: f.Usuario || "",
      cpf: f.CPF || "",
      email: f.Email || "",
      telefone: f.Telefone || "",
      celular: f.Celular || "",
      cep: f.CEP || "",
      rua: f.Rua || "",
      numero: f.Numero || "",
      bairro: f.Bairro || "",
      cidade: f.Cidade || "",
      estado: f.Estado || "",
      cargo: f.Cargo || "",
      setorId: f.IdSetor != null ? String(f.IdSetor) : "",
      nivel: f.Nivel != null ? String(f.Nivel) : "",
      nivelParametro: normalizarNivelParametro(
        f.NivelParametro ?? f.nivelParametro ?? 0
      ),
      situacao: f.Situacao || "Ativo",
      senha: "",
      confirmarSenha: "",
      codFuncNew: f.CodFunc_New ?? "",
      codCad: f.CodCad ?? "",
      bloqueado: normalizaBloqueado(f.Bloqueado),
      dmControlaPonto: normalizaSN(f.DM_CONTROLA_PONTO ?? f.dmControlaPonto),
      dmExibeLembretePonto: normalizaSN(
        f.DM_EXIBE_LEMBRETE_PONTO ?? f.dmExibeLembretePonto
      ),
    };

    dlog("Map from API → form =", mapped);
    return mapped;
  };


  const getOperadorPayload = () => ({
    AtualizadoPor:
      sessionStorage.getItem("usuario") ||
      sessionStorage.getItem("Usuario") ||
      sessionStorage.getItem("Nome") ||
      "web",
    CriadoPor:
      sessionStorage.getItem("usuario") ||
      sessionStorage.getItem("Usuario") ||
      sessionStorage.getItem("Nome") ||
      "web",
    NivelOperador: sessionStorage.getItem("NivelFunc") || "",
    CodFuncOperador:
      sessionStorage.getItem("codFunc") || sessionStorage.getItem("CodFunc") || "",
    IdFuncionarioOperador:
      sessionStorage.getItem("IdFuncionario") ||
      sessionStorage.getItem("idFuncionario") ||
      null,
  });

  const getCodFuncPermissao = (target = form) =>
    String(
      target?.codFunc ||
        target?.CodFunc ||
        target?.codFuncNew ||
        target?.seuCodigo ||
        target?.id ||
        ""
    ).trim();

  const funcionarioPermissoesBloqueado =
    !form.id || form.bloqueado || String(form.situacao || "").toUpperCase() !== "ATIVO";

  const carregarPermissoesAgenda = async (target = form) => {
    if (!target?.id) return;

    const codFunc = getCodFuncPermissao(target);

    setLoadingPermissoes(true);

    try {
      let data;

      try {
        const resp = await api.get(
          `/permissoes/funcionario/${target.id}/modulo/${MODULO_AGENDA}`
        );
        data = resp.data;
      } catch (err) {
        const status = err?.response?.status;

        if (![404, 405].includes(status)) throw err;

        const resp = await api.get("/permissoes/agenda", {
          params: { idFuncionario: target.id, codFunc },
        });
        data = resp.data;
      }

      const root = data?.data ?? data ?? {};

      setPermissoesAgenda(extrairPermissoesAgenda(root));
      setPermissoesMeta({
        vigenciaInicio: root.vigenciaInicio || "",
        vigenciaFim: root.vigenciaFim || "",
        motivo: root.motivo || "",
        observacao: root.observacao || "",
        escopo: root.escopo || "GLOBAL",
        valorEscopo: root.valorEscopo || "",
        permissoesDetalhe: Array.isArray(root.permissoesDetalhe)
          ? root.permissoesDetalhe
          : [],
      });
      setPermissoesDirty(false);
    } catch (err) {
      setPermissoesAgenda(DEFAULT_PERMISSOES_AGENDA);
      setPermissoesMeta(DEFAULT_PERMISSOES_META);
      setPermissoesDirty(false);
      showError(
        err?.response?.data?.message ||
          "Falha ao carregar permissões do funcionário."
      );
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const togglePermissaoAgenda = (boolKey) => {
    if (!isADM) return showError("Apenas ADM pode alterar permissões.");
    if (!form.id) return showError("Selecione um funcionário.");
    if (funcionarioPermissoesBloqueado) {
      return showError(
        "Funcionário precisa estar ativo e desbloqueado para receber permissões."
      );
    }

    setPermissoesAgenda((p) => ({ ...p, [boolKey]: !p[boolKey] }));
    setPermissoesDirty(true);
  };

  const aplicarPerfilAgenda = (perfil) => {
    if (!isADM) return showError("Apenas ADM pode alterar permissões.");
    if (!form.id) return showError("Selecione um funcionário.");
    if (funcionarioPermissoesBloqueado) {
      return showError(
        "Funcionário precisa estar ativo e desbloqueado para receber permissões."
      );
    }

    setPermissoesAgenda(perfilPermissoesAgenda(perfil));
    setPermissoesDirty(true);

    setPermissoesMeta((p) => ({
      ...p,
      motivo:
        p.motivo ||
        (perfil === "SEM_ACESSO"
          ? "Remoção de acesso à agenda"
          : "Ajuste de perfil de agenda"),
    }));
  };

  const validarPermissoesAgenda = () => {
    if (!isADM) return "Apenas ADM pode alterar permissões.";
    if (!form.id) return "Selecione um funcionário.";

    if (funcionarioPermissoesBloqueado) {
      return "Funcionário precisa estar ativo e desbloqueado para receber permissões.";
    }

    if (!String(permissoesMeta.motivo || "").trim()) {
      return "Informe o motivo da alteração para auditoria.";
    }

    if (permissoesMeta.vigenciaInicio && permissoesMeta.vigenciaFim) {
      const ini = new Date(permissoesMeta.vigenciaInicio).getTime();
      const fim = new Date(permissoesMeta.vigenciaFim).getTime();

      if (ini > fim) return "Vigência final não pode ser menor que a inicial.";
    }

    return null;
  };

  const salvarPermissoesAgenda = async () => {
    if (salvandoPermissoes) return;

    const msg = validarPermissoesAgenda();
    if (msg) return showError(msg);

    const codFunc = getCodFuncPermissao(form);
    const payload = {
      permissoes: permissaoAgendaParaApi(permissoesAgenda),
      escopo: "GLOBAL",
      vigenciaInicio: permissoesMeta.vigenciaInicio || null,
      vigenciaFim: permissoesMeta.vigenciaFim || null,
      motivo: String(permissoesMeta.motivo || "").trim(),
      observacao: String(permissoesMeta.observacao || "").trim() || null,
      ...getOperadorPayload(),
    };

    setSalvandoPermissoes(true);

    try {
      try {
        await api.put(
          `/permissoes/funcionario/${form.id}/modulo/${MODULO_AGENDA}`,
          payload
        );
      } catch (err) {
        const status = err?.response?.status;

        if (![404, 405].includes(status)) throw err;

        await api.post("/permissoes/agenda", {
          idFuncionario: Number(form.id),
          codFunc,
          ...payload,
        });
      }

      showSuccess("Permissões de agenda salvas.");
      setPermissoesDirty(false);
      await carregarPermissoesAgenda(form);
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao salvar permissões.");
    } finally {
      setSalvandoPermissoes(false);
    }
  };

  const revogarPermissoesAgenda = async () => {
    if (!isADM) return showError("Apenas ADM pode revogar permissões.");
    if (!form.id) return showError("Selecione um funcionário.");

    const ok = window.confirm(
      "Revogar todas as permissões de agenda deste funcionário?"
    );

    if (!ok) return;

    const codFunc = getCodFuncPermissao(form);
    const payload = {
      acoes: AGENDA_PERMISSOES.map((p) => p.key),
      motivo:
        String(permissoesMeta.motivo || "").trim() ||
        "Revogação manual de permissões de agenda",
      observacao:
        String(permissoesMeta.observacao || "").trim() ||
        "Permissões revogadas pela tela de cadastro de funcionários.",
      ...getOperadorPayload(),
    };

    setSalvandoPermissoes(true);

    try {
      try {
        await api.post(
          `/permissoes/funcionario/${form.id}/modulo/${MODULO_AGENDA}/revogar`,
          payload
        );
      } catch (err) {
        const status = err?.response?.status;

        if (![404, 405].includes(status)) throw err;

        await api.post("/permissoes/agenda/revogar", {
          idFuncionario: Number(form.id),
          codFunc,
          ...payload,
        });
      }

      setPermissoesAgenda(DEFAULT_PERMISSOES_AGENDA);
      setPermissoesDirty(false);
      showSuccess("Permissões de agenda revogadas.");
      await carregarPermissoesAgenda(form);
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao revogar permissões.");
    } finally {
      setSalvandoPermissoes(false);
    }
  };

  const validarCreate = () => {
    if (!form.nome?.trim()) return "Nome é obrigatório.";
    if (digits(form.cpf).length !== 11) return "CPF inválido.";
    if (!form.email?.includes("@")) return "Email inválido.";
    if (!form.nivel) return "Nível é obrigatório.";
    if ((form.senha || "").length < 8) return "Senha mínima de 8 caracteres.";
    if (form.senha !== form.confirmarSenha) return "Senhas não conferem.";
    if (upper2(form.estado).length !== 2) return "UF inválida.";
    return null;
  };

  const validarUpdate = () => {
    if (!form.id) return "Registro não selecionado.";
    if (!form.email?.includes("@")) return "Email inválido.";
    if (!form.nivel) return "Nível é obrigatório.";
    if (upper2(form.estado).length !== 2) return "UF inválida.";
    return null;
  };

  const onSalvar = async () => {
    if (salvando) return;

    try {
      setSalvando(true);

      if (!form.id) {
        const msg = validarCreate();
        if (msg) return showError(msg);

        const payload = toCreatePayload();

        dlog("POST /func payload=", payload);

        const { data } = await api.post("/func", payload);
        const loaded = fromApiToForm(data);

        setForm(loaded);
        setSelecionado(data);
        setHor((h) => ({ ...h, NomeFunc: loaded.nome || "" }));

        showSuccess("Funcionário cadastrado.");
      } else {
        const msg = validarUpdate();
        if (msg) return showError(msg);

        const payload = toUpdatePayload();

        dlog("PUT /func/:id", form.id, "payload=", payload);

        await api.put(`/func/${form.id}`, payload);

        showSuccess("Funcionário atualizado.");
      }
    } catch (err) {
      const status = err.response?.status;

      if (status === 409) {
        return showError(err.response?.data?.message || "Registro duplicado.");
      }

      if (status === 400) return showError("Payload inválido.");
      if (status === 403) return showError("Sem permissão.");

      showError("Falha ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const onSalvarNivelParametro = async () => {
    if (!isADM) {
      return showError(
        "Apenas ADM pode definir o nível de permissão para alteração de parâmetros."
      );
    }

    if (!form.id) {
      return showError(
        "Salve ou selecione o funcionário antes de definir o Nível de Parâmetros."
      );
    }

    const nivelParametro = normalizarNivelParametro(form.nivelParametro);

    const opcao = NIVEL_PARAMETRO_OPTS.find(
      (item) => item.value === nivelParametro
    );

    const ok = window.confirm(
      `Confirmar Nível de Parâmetros N${nivelParametro} para ${form.nome || "este funcionário"}?\n\n${opcao?.desc || ""}`
    );

    if (!ok) return;

    setSalvandoNivelParametro(true);

    try {
      const { data } = await api.patch(
  `/func/${form.id}/nivel-parametro`,
  {
    NivelParametro: nivelParametro,
  }
);

      setForm((prev) => ({
        ...prev,
        nivelParametro: normalizarNivelParametro(
          data?.NivelParametro ?? nivelParametro
        ),
      }));

      showSuccess(
        data?.message ||
          `Nível de Parâmetros atualizado para N${nivelParametro}.`
      );
    } catch (err) {
      const status = err?.response?.status;

      if (status === 401 || status === 403) {
        return showError(
          err?.response?.data?.message ||
            "Somente ADM pode alterar o Nível de Parâmetros."
        );
      }

      showError(
        err?.response?.data?.message ||
          "Falha ao atualizar o Nível de Parâmetros."
      );
    } finally {
      setSalvandoNivelParametro(false);
    }
  };

  const onAlterarBloqueio = async () => {
    if (!isADM) return showError("Apenas ADM pode alterar bloqueio.");
    if (!form.id) return showError("Selecione um funcionário.");

    const novoBloqueio = form.bloqueado ? 0 : 1;

    const msg = novoBloqueio
      ? "Confirmar bloqueio deste funcionário?"
      : "Confirmar desbloqueio deste funcionário?";

    if (!window.confirm(msg)) return;

    setSalvandoBloqueio(true);

    try {
      await api.patch(`/func/${form.id}/bloqueio`, {
        bloqueado: novoBloqueio,
        AtualizadoPor: sessionStorage.getItem("usuario") || "web",
      });

      setForm((p) => ({ ...p, bloqueado: novoBloqueio }));

      showSuccess(
        novoBloqueio
          ? "Funcionário bloqueado com sucesso."
          : "Funcionário desbloqueado com sucesso."
      );
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao alterar bloqueio.");
    } finally {
      setSalvandoBloqueio(false);
    }
  };

  const onDesbloquearRapido = async () => {
    if (!isADM) return showError("Apenas ADM pode desbloquear funcionário.");
    if (!form.id) return showError("Selecione um funcionário.");

    if (!window.confirm("Confirmar desbloqueio operacional deste funcionário?")) {
      return;
    }

    setSalvandoBloqueio(true);

    try {
      await api.post(`/func/${form.id}/desbloquear`, {
        AtualizadoPor: sessionStorage.getItem("usuario") || "web",
      });

      setForm((p) => ({ ...p, bloqueado: 0 }));

      showSuccess("Funcionário desbloqueado com sucesso.");
    } catch (err) {
      showError(err?.response?.data?.message || "Falha ao desbloquear.");
    } finally {
      setSalvandoBloqueio(false);
    }
  };

  const onAlterarSenhaSelf = async () => {
    if (!form.id) return showError("Selecione um funcionário.");
    if ((form.senha || "").length < 8)
      return showError("Nova senha mínima de 8 caracteres.");
    if (form.senha !== form.confirmarSenha)
      return showError("Senhas não conferem.");
    if (!senhaAtual) return showError("Informe a senha atual.");

    try {
      const payload = {
        SenhaAtual: senhaAtual,
        NovaSenha: form.senha,
        Confirmar: form.confirmarSenha,
      };

      const { data } = await api.put(`/func/${form.id}/senha-self`, payload);

      setForm((p) => ({ ...p, senha: "", confirmarSenha: "" }));
      setSenhaAtual("");

      showSuccess(data?.message || "Senha alterada. Sessões revogadas.");
    } catch (err) {
      const status = err.response?.status;

      if (status === 401) return showError("Senha atual inválida.");
      if (status === 422)
        return showError(
          err.response?.data?.message || "Nova senha já utilizada recentemente."
        );

      showError(err?.response?.data?.message || "Falha ao alterar senha.");
    }
  };

  const onInativar = async () => {
    if (!form.id) return showError("Selecione um funcionário.");

    const ok = window.confirm("Confirmar inativação (soft delete)?");
    if (!ok) return;

    try {
      await api.delete(`/func/${form.id}`);
      showInfo("Funcionário inativado.");
      limpar();
    } catch {
      showError("Falha ao inativar.");
    }
  };

  const onEnter = (e) => {
    if (e.key !== "Enter") return;
    return onSalvar();
  };

  const onSelecionar = async (func) => {
    const id =
      func?.IdFuncionario ??
      func?.id ??
      func?.value ??
      (typeof func === "number" ? func : null);

    setSelecionado(func || null);

    if (!id) {
      limpar();
      return;
    }

    try {
      const { data } = await api.get(`/func/${id}`);
      const loaded = fromApiToForm(data);

      setForm(loaded);

      setSelecionado({
        ...(func || {}),
        IdFuncionario: id,
        label: data?.Nome || func?.label,
      });

      if (!loaded.setorId && loaded.cargo && setores.length) {
        const hit = setores.find((s) => eqi(s.nome, loaded.cargo));
        if (hit) setForm((p) => ({ ...p, setorId: String(hit.id) }));
      }

      await carregarPermissoesAgenda(loaded);
    } catch (err) {
      showError(
        err?.response?.data?.message || "Falha ao carregar funcionário."
      );
    }
  };

  const carregarHorarios = async (id, nome) => {
    if (!id) return;

    setLoadingHor(true);

    try {
      const { data, status } = await api.get(`/func-horario/func/${id}/horario`);

      const hasObject =
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        Object.keys(data).length > 0;

      if (status === 204 || !hasObject) {
        setHor({ ...DEFAULT_HOR, NomeFunc: nome ?? "" });
      } else {
        setHor({
          NomeFunc: data.NomeFunc ?? nome ?? "",
          EntradaManha: data.EntradaManha ?? "",
          SaidaManha: data.SaidaManha ?? "",
          EntradaTarde: data.EntradaTarde ?? "",
          SaidaTarde: data.SaidaTarde ?? "",
          TempoAlmoco: data.TempoAlmoco ?? "",
          Tolerancia: data.Tolerancia ?? "",
        });
      }
    } catch {
      setHor({ ...DEFAULT_HOR, NomeFunc: nome ?? "" });
    } finally {
      setLoadingHor(false);
    }
  };

  const HHMM = /^\d{2}:\d{2}$/;
  const v = (s) => (s ?? "").trim();

  const validarHor = () => {
    const keys = [
      "EntradaManha",
      "SaidaManha",
      "EntradaTarde",
      "SaidaTarde",
      "TempoAlmoco",
      "Tolerancia",
    ];

    for (const k of keys) {
      const val = v(hor[k]);
      if (val && !HHMM.test(val)) return `Campo ${k} inválido. Use HH:mm.`;
    }

    return null;
  };

  const salvarHorarios = async () => {
    if (!form.id) return showError("Salve o cadastro antes.");

    const msg = validarHor();
    if (msg) return showError(msg);

    await api.put(`/func-horario/func/${form.id}/horario`, { ...hor });

    showSuccess("Horários salvos.");
  };

  const removerHorarios = async () => {
    if (!form.id) return showError("Salve o cadastro antes.");

    const ok = window.confirm("Remover horários deste funcionário?");
    if (!ok) return;

    await api.delete(`/func-horario/func/${form.id}/horario`);
    setHor({ ...DEFAULT_HOR, NomeFunc: form.nome || "" });

    showSuccess("Horários removidos.");
  };

  const confirmInvalid =
    !!form.confirmarSenha && form.senha !== form.confirmarSenha;

  const setorOptions = useMemo(
    () =>
      setores.map((s) => (
        <option key={s.id} value={s.id}>
          {s.nome}
        </option>
      )),
    [setores]
  );

  const BadgeReadonly = ({ label, value }) => (
    <div className="badge-ro">
      <span className="label">{label}</span>
      <span className="value">{value ?? "-"}</span>
      <button
        type="button"
        className="copy"
        onClick={() => {
          if (!value) return;
          navigator.clipboard.writeText(String(value));
          showInfo("Copiado.");
        }}
        title="Copiar"
        aria-label="Copiar"
      >
        <Copy size={14} />
      </button>
    </div>
  );

  return (
    <MainLayout>
      <div className="func-page">
        <div className="func-toolbar">
          <div className="left">
            <h1>Cadastro de Funcionário</h1>
            {form.id && (
              <div className="func-status-line">
                <span
                  className={`status-pill ${
                    form.bloqueado ? "danger" : "success"
                  }`}
                >
                  {form.bloqueado ? "Bloqueado" : "Liberado"}
                </span>

                <span className="status-pill neutral">
                  Ponto: {form.dmControlaPonto === "S" ? "Controlado" : "Livre"}
                </span>

                <span className="status-pill neutral">
                  Lembrete:{" "}
                  {form.dmExibeLembretePonto === "S" ? "Ativo" : "Inativo"}
                </span>
              </div>
            )}
          </div>

          <div className="right">
            <Button variant="secondary" onClick={limpar}>
              Novo
            </Button>

            <Button onClick={onSalvar} disabled={salvando}>
              {salvando ? "Salvando..." : form.id ? "Atualizar" : "Gravar"}
            </Button>

            <Button
              variant="destructive"
              onClick={onInativar}
              disabled={!form.id}
            >
              Inativar
            </Button>
          </div>
        </div>

        <div className="func-search card">
          <Label>Buscar</Label>
          <SelectFuncionario
            value={selecionado}
            onChange={onSelecionar}
            includeBloqueados={isADM}
            pageSize={10}
            minChars={2}
          />
          {isADM && (
            <small className="helper-text">
              Perfil ADM: a busca inclui funcionários bloqueados para manutenção
              operacional.
            </small>
          )}
        </div>

        <div className="tabs-header card">
          <button
            type="button"
            className={`tab-btn ${activeTab === "INFO" ? "active" : ""}`}
            onClick={() => setActiveTab("INFO")}
          >
            Informações
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "HOR" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("HOR");
              carregarHorarios(form.id, form.nome);
            }}
            disabled={!form.id}
            title={!form.id ? "Salve o cadastro para habilitar" : ""}
          >
            Horários
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "PERM" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("PERM");
              carregarPermissoesAgenda(form);
            }}
            disabled={!form.id}
            title={!form.id ? "Salve o cadastro para habilitar" : ""}
          >
            <ShieldCheck size={15} /> Permissões
          </button>
        </div>

        {activeTab === "INFO" && (
          <>
            <section className="card section">
              <div className="section-title">Identificação</div>

              <div className="grid grid-4">
                <div>
                  <Label>Id</Label>
                  <Input
                    name="id"
                    value={form.id}
                    readOnly
                    placeholder="IdFuncionario"
                  />
                </div>

                <div>
                  <Label>Seu código</Label>
                  <Input
                    name="seuCodigo"
                    value={form.seuCodigo}
                    readOnly
                    placeholder="CodFunc_New"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Nome</Label>
                  <Input
                    name="nome"
                    value={form.nome}
                    onChange={handleChange}
                    onKeyDown={onEnter}
                    placeholder="Nome completo"
                  />
                </div>

                <div>
                  <Label>Usuário</Label>
                  <Input
                    name="usuario"
                    value={form.usuario}
                    onChange={handleChange}
                    placeholder="login"
                  />
                </div>

                <div>
                  <Label>CPF</Label>
                  <Input
                    name="cpf"
                    value={form.cpf}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Email</Label>
                  <Input
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    onKeyDown={onEnter}
                    placeholder="email@empresa.com"
                  />
                </div>

                <div className="col-span-1">
                  <div
                    style={{ display: "flex", alignItems: "flex-end", gap: 8 }}
                  >
                    <div style={{ flex: 1 }}>
                      <Label>Setor</Label>
                      <select
                        className="input native"
                        name="setorId"
                        value={form.setorId}
                        onChange={handleChange}
                        disabled={loadingSetores}
                      >
                        <option value="">
                          {loadingSetores ? "Carregando..." : "Selecione"}
                        </option>
                        {setorOptions}
                      </select>
                    </div>

                    <div>
                      <Label>&nbsp;</Label>
                      <button
                        type="button"
                        className="btn-outline-plain"
                        title="Gerenciar Setores"
                        aria-label="Gerenciar Setores"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSetores(true);
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Settings size={16} /> Gerenciar
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Nível</Label>
                  <select
                    className="input native"
                    name="nivel"
                    value={form.nivel}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    {NIVEL_OPTS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Situação</Label>
                  <select
                    className="input native"
                    name="situacao"
                    value={form.situacao}
                    onChange={handleChange}
                  >
                    {SITUACAO_OPTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {form.id && (
                  <div className="col-span-4 badges-row">
                    <BadgeReadonly label="CodFunc" value={form.codFunc} />
                    <BadgeReadonly label="Seu código" value={form.codFuncNew} />
                    <BadgeReadonly label="CodCad" value={form.codCad} />
                  </div>
                )}
              </div>
            </section>

            <section className="card section admin-section">
              <div className="section-title">
                Controle Administrativo
                {!isADM && (
                  <span className="section-subtitle">
                    Somente ADM pode alterar estas configurações.
                  </span>
                )}
              </div>

              <div className="grid grid-4">
                <div>
                  <Label>Controla ponto?</Label>
                  <select
                    className="input native"
                    name="dmControlaPonto"
                    value={form.dmControlaPonto}
                    onChange={handleChange}
                    disabled={!isADM}
                  >
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>

                <div>
                  <Label>Exibe lembrete de ponto?</Label>
                  <select
                    className="input native"
                    name="dmExibeLembretePonto"
                    value={form.dmExibeLembretePonto}
                    onChange={handleChange}
                    disabled={!isADM}
                  >
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>

                <div className="nivel-parametro-admin col-span-2">
                  <div className="nivel-parametro-admin-head">
                    <div className="nivel-parametro-admin-title">
                      <ShieldCheck size={17} />
                      <div>
                        <Label>Nível de Parâmetros</Label>
                        <small>
                          Independente do perfil geral {form.nivel || "do funcionário"}.
                        </small>
                      </div>
                    </div>

                    <span className={`nivel-parametro-chip nivel-${form.nivelParametro}`}>
                      N{normalizarNivelParametro(form.nivelParametro)}
                    </span>
                  </div>

                  <div className="nivel-parametro-admin-controls">
                    <select
                      className="input native"
                      name="nivelParametro"
                      value={normalizarNivelParametro(form.nivelParametro)}
                      onChange={handleChange}
                      disabled={!isADM || !form.id || salvandoNivelParametro}
                    >
                      {NIVEL_PARAMETRO_OPTS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={onSalvarNivelParametro}
                      disabled={!isADM || !form.id || salvandoNivelParametro}
                    >
                      <ShieldCheck size={15} />
                      {salvandoNivelParametro ? "Salvando..." : "Salvar nível"}
                    </Button>
                  </div>

                  <small className="nivel-parametro-help">
                    {form.id
                      ? NIVEL_PARAMETRO_OPTS.find(
                          (item) =>
                            item.value ===
                            normalizarNivelParametro(form.nivelParametro),
                        )?.desc
                      : "Novo funcionário inicia em N0. Salve o cadastro primeiro; depois um ADM poderá definir este nível."}
                  </small>

                  {!isADM && (
                    <small className="nivel-parametro-warning">
                      Somente ADM pode definir ou alterar este nível.
                    </small>
                  )}
                </div>

                <div>
                  <Label>Status de acesso</Label>
                  <div
                    className={`access-status ${
                      form.bloqueado ? "blocked" : "released"
                    }`}
                  >
                    {form.bloqueado ? "Bloqueado" : "Liberado"}
                  </div>
                </div>

                <div className="admin-actions">
                  <Label>&nbsp;</Label>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      variant={form.bloqueado ? "outline" : "destructive"}
                      onClick={onAlterarBloqueio}
                      disabled={!isADM || !form.id || salvandoBloqueio}
                    >
                      {salvandoBloqueio
                        ? "Processando..."
                        : form.bloqueado
                        ? "Desbloquear"
                        : "Bloquear"}
                    </Button>

                    {form.bloqueado ? (
                      <Button
                        variant="outline"
                        onClick={onDesbloquearRapido}
                        disabled={!isADM || !form.id || salvandoBloqueio}
                      >
                        Desbloqueio rápido
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section className="card section">
              <div className="section-title">Contato</div>

              <div className="grid grid-3">
                <div>
                  <Label>Telefone</Label>
                  <Input
                    name="telefone"
                    value={form.telefone}
                    onChange={handleChange}
                    placeholder="(00) 0000-0000"
                  />
                </div>

                <div>
                  <Label>Celular</Label>
                  <Input
                    name="celular"
                    value={form.celular}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="placeholder" />
              </div>
            </section>

            <section className="card section">
              <div className="section-title">Endereço</div>

              <div className="grid grid-4">
                <div className="rel">
                  <Label>CEP</Label>
                  <Input
                    name="cep"
                    value={form.cep}
                    onChange={handleChange}
                    onBlur={buscarCEP}
                    placeholder="00000-000"
                    className="ssw-input with-spinner cep-input"
                  />
                  {loadingCep && <div className="spinner" />}
                </div>

                <div className="col-span-2">
                  <Label>Rua</Label>
                  <Input
                    name="rua"
                    value={form.rua}
                    onChange={handleChange}
                    placeholder="Logradouro"
                  />
                </div>

                <div>
                  <Label>Número</Label>
                  <Input
                    name="numero"
                    value={form.numero}
                    onChange={handleChange}
                    placeholder="Nº"
                  />
                </div>

                <div>
                  <Label>Bairro</Label>
                  <Input
                    name="bairro"
                    value={form.bairro}
                    onChange={handleChange}
                    placeholder="Bairro"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Cidade</Label>
                  <Input
                    name="cidade"
                    value={form.cidade}
                    onChange={handleChange}
                    placeholder="Cidade"
                  />
                </div>

                <div>
                  <Label>Estado (UF)</Label>
                  <Input
                    name="estado"
                    value={form.estado}
                    onChange={handleChange}
                    placeholder="UF"
                  />
                </div>
              </div>
            </section>

            <section className="card section">
              <div className="section-title">
                {form.id
                  ? "Trocar Senha (self-service)"
                  : "Definir Senha (novo cadastro)"}
              </div>

              {!form.id ? (
                <div className="grid grid-2 password-row">
                  <div className="rel password-field">
                    <Label>Senha</Label>
                    <Input
                      name="senha"
                      type={mostrarSenha ? "text" : "password"}
                      value={form.senha}
                      onChange={handleChange}
                      onKeyDown={onEnter}
                      className="ssw-input input-with-icon"
                      placeholder="Senha"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="icon-eye btn-eye"
                      aria-label={
                        mostrarSenha ? "Ocultar senha" : "Mostrar senha"
                      }
                      title={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                    >
                      {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className="rel password-field">
                    <Label>Confirmar Senha</Label>
                    <Input
                      name="confirmarSenha"
                      type={mostrarConfirmacao ? "text" : "password"}
                      value={form.confirmarSenha}
                      onChange={handleChange}
                      onKeyDown={onEnter}
                      className={`ssw-input input-with-icon ${
                        confirmInvalid ? "is-invalid" : ""
                      }`}
                      placeholder="Confirmação"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="icon-eye btn-eye"
                      aria-label={
                        mostrarConfirmacao
                          ? "Ocultar confirmação"
                          : "Mostrar confirmação"
                      }
                      title={
                        mostrarConfirmacao
                          ? "Ocultar confirmação"
                          : "Mostrar confirmação"
                      }
                      onClick={() => setMostrarConfirmacao(!mostrarConfirmacao)}
                    >
                      {mostrarConfirmacao ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-3 password-row">
                  <div className="rel password-field">
                    <Label>Senha Atual</Label>
                    <Input
                      name="senhaAtual"
                      type={mostrarSenhaAtual ? "text" : "password"}
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      className="ssw-input input-with-icon"
                      placeholder="Senha atual"
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      className="icon-eye btn-eye"
                      aria-label={mostrarSenhaAtual ? "Ocultar" : "Mostrar"}
                      title={mostrarSenhaAtual ? "Ocultar" : "Mostrar"}
                      onClick={() => setMostrarSenhaAtual(!mostrarSenhaAtual)}
                    >
                      {mostrarSenhaAtual ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>

                  <div className="rel password-field">
                    <Label>Nova Senha</Label>
                    <Input
                      name="senha"
                      type={mostrarSenha ? "text" : "password"}
                      value={form.senha}
                      onChange={handleChange}
                      className="ssw-input input-with-icon"
                      placeholder="Nova senha"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="icon-eye btn-eye"
                      aria-label={
                        mostrarSenha ? "Ocultar senha" : "Mostrar senha"
                      }
                      title={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                    >
                      {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className="rel password-field">
                    <Label>Confirmar</Label>
                    <Input
                      name="confirmarSenha"
                      type={mostrarConfirmacao ? "text" : "password"}
                      value={form.confirmarSenha}
                      onChange={handleChange}
                      className={`ssw-input input-with-icon ${
                        confirmInvalid ? "is-invalid" : ""
                      }`}
                      placeholder="Confirmação"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="icon-eye btn-eye"
                      aria-label={
                        mostrarConfirmacao
                          ? "Ocultar confirmação"
                          : "Mostrar confirmação"
                      }
                      title={
                        mostrarConfirmacao
                          ? "Ocultar confirmação"
                          : "Mostrar confirmação"
                      }
                      onClick={() => setMostrarConfirmacao(!mostrarConfirmacao)}
                    >
                      {mostrarConfirmacao ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>

                  <div
                    className="col-span-3"
                    style={{ display: "flex", gap: 8, marginTop: 8 }}
                  >
                    <Button
                      variant="outline"
                      onClick={onAlterarSenhaSelf}
                      disabled={!form.id}
                    >
                      Alterar Senha
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </>
        )}


        {activeTab === "PERM" && (
          <section className="card section permissions-section">
            <div className="section-title permissions-title">
              <span>
                <ShieldCheck size={17} /> Permissões de Agenda
              </span>

              <div className="permissions-title-actions">
                {permissoesDirty && (
                  <span className="status-pill warning">Alterações não salvas</span>
                )}

                <Button
                  variant="outline"
                  onClick={() => carregarPermissoesAgenda(form)}
                  disabled={!form.id || loadingPermissoes}
                >
                  <RefreshCcw size={15} />
                  {loadingPermissoes ? "Carregando..." : "Recarregar"}
                </Button>
              </div>
            </div>

            {!form.id ? (
              <div className="perm-empty-state">
                Selecione ou grave um funcionário antes de configurar permissões.
              </div>
            ) : (
              <>
                <div className="perm-context-card">
                  <div>
                    <span className="perm-context-label">Funcionário</span>
                    <strong>{form.nome || "-"}</strong>
                    <small>
                      ID {form.id || "-"} · CodFunc {getCodFuncPermissao(form) || "-"} · Nível {form.nivel || "-"} · Parâmetros N{normalizarNivelParametro(form.nivelParametro)}
                    </small>
                    <small>
                      Vigência: {fmtVigenciaPermissao(permissoesMeta.vigenciaInicio, permissoesMeta.vigenciaFim)}
                      {permissoesMeta.motivo ? ` · ${permissoesMeta.motivo}` : ""}
                    </small>
                  </div>

                  <div className="perm-context-badges">
                    <span
                      className={`status-pill ${
                        form.situacao === "Ativo" ? "success" : "warning"
                      }`}
                    >
                      {form.situacao || "-"}
                    </span>

                    <span
                      className={`status-pill ${
                        form.bloqueado ? "danger" : "success"
                      }`}
                    >
                      {form.bloqueado ? "Bloqueado" : "Liberado"}
                    </span>
                  </div>
                </div>

                {!isADM && (
                  <div className="perm-alert warning">
                    <AlertTriangle size={17} />
                    Apenas usuários ADM podem atribuir ou revogar permissões nesta tela.
                  </div>
                )}

                {funcionarioPermissoesBloqueado && (
                  <div className="perm-alert danger">
                    <AlertTriangle size={17} />
                    Este funcionário não está apto para receber permissões agora. Ele precisa estar Ativo e Liberado.
                  </div>
                )}

                <div className="perm-quick-actions">
                  <button
                    type="button"
                    onClick={() => aplicarPerfilAgenda("OPERADOR")}
                    disabled={!isADM || funcionarioPermissoesBloqueado}
                  >
                    Operador
                    <small>Criar, editar, confirmar e finalizar</small>
                  </button>

                  <button
                    type="button"
                    onClick={() => aplicarPerfilAgenda("RESPONSAVEL")}
                    disabled={!isADM || funcionarioPermissoesBloqueado}
                  >
                    Responsável
                    <small>Agenda completa sem gerenciar permissões</small>
                  </button>

                  <button
                    type="button"
                    onClick={() => aplicarPerfilAgenda("ADMIN_AGENDA")}
                    disabled={!isADM || funcionarioPermissoesBloqueado}
                  >
                    Admin agenda
                    <small>Inclui gerenciar permissões</small>
                  </button>

                  <button
                    type="button"
                    className="danger"
                    onClick={() => aplicarPerfilAgenda("SEM_ACESSO")}
                    disabled={!isADM || funcionarioPermissoesBloqueado}
                  >
                    Sem acesso
                    <small>Remove marcações antes de salvar</small>
                  </button>
                </div>

                <div className="permissions-grid">
                  {AGENDA_PERMISSOES.map((perm) => {
                    const checked = !!permissoesAgenda[perm.boolKey];

                    return (
                      <button
                        type="button"
                        key={perm.key}
                        className={`permission-card ${checked ? "active" : ""} ${
                          perm.danger ? "danger-zone" : ""
                        }`}
                        onClick={() => togglePermissaoAgenda(perm.boolKey)}
                        disabled={!isADM || funcionarioPermissoesBloqueado || loadingPermissoes}
                      >
                        <span className="permission-card-top">
                          <span className="permission-icon">
                            {perm.danger ? <KeyRound size={16} /> : <ShieldCheck size={16} />}
                          </span>
                          <span className={`permission-state ${checked ? "on" : "off"}`}>
                            {checked ? "Liberado" : "Bloqueado"}
                          </span>
                        </span>

                        <strong>{perm.label}</strong>
                        <small>{perm.desc}</small>

                        {perm.danger && checked && (
                          <em>Permissão crítica: libera gestão de permissões.</em>
                        )}
                      </button>
                    );
                  })}
                </div>

                {permissoesMeta.permissoesDetalhe?.length ? (
                  <div className="perm-alert info">
                    <ShieldCheck size={17} />
                    Permissões carregadas da API. Vigência principal: {fmtVigenciaPermissao(permissoesMeta.vigenciaInicio, permissoesMeta.vigenciaFim)}.
                  </div>
                ) : null}

                <div className="perm-meta-grid">
                  <div>
                    <Label>Início da vigência</Label>
                    <Input
                      type="datetime-local"
                      value={toInputDateTime(permissoesMeta.vigenciaInicio)}
                      onChange={(e) => {
                        setPermissoesMeta((p) => ({
                          ...p,
                          vigenciaInicio: e.target.value,
                        }));
                        setPermissoesDirty(true);
                      }}
                      disabled={!isADM || funcionarioPermissoesBloqueado}
                    />
                    <small>Vazio = agora.</small>
                  </div>

                  <div>
                    <Label>Fim da vigência</Label>
                    <Input
                      type="datetime-local"
                      value={toInputDateTime(permissoesMeta.vigenciaFim)}
                      onChange={(e) => {
                        setPermissoesMeta((p) => ({
                          ...p,
                          vigenciaFim: e.target.value,
                        }));
                        setPermissoesDirty(true);
                      }}
                      disabled={!isADM || funcionarioPermissoesBloqueado}
                    />
                    <small>Vazio = sem vencimento.</small>
                  </div>

                  <div className="col-span-2">
                    <Label>Motivo da alteração</Label>
                    <Input
                      value={permissoesMeta.motivo}
                      onChange={(e) => {
                        setPermissoesMeta((p) => ({ ...p, motivo: e.target.value }));
                        setPermissoesDirty(true);
                      }}
                      placeholder="Ex.: Responsável pela agenda, férias, substituição, revogação"
                      disabled={!isADM || funcionarioPermissoesBloqueado}
                    />
                  </div>

                  <div className="col-span-4">
                    <Label>Observação interna</Label>
                    <textarea
                      className="ssw-input ssw-textarea perm-textarea"
                      value={permissoesMeta.observacao}
                      onChange={(e) => {
                        setPermissoesMeta((p) => ({
                          ...p,
                          observacao: e.target.value,
                        }));
                        setPermissoesDirty(true);
                      }}
                      placeholder="Detalhe a decisão para auditoria. Ex.: cobertura temporária durante férias do responsável."
                      disabled={!isADM || funcionarioPermissoesBloqueado}
                    />
                  </div>
                </div>

                <div className="perm-footer-actions">
                  <Button
                    onClick={salvarPermissoesAgenda}
                    disabled={
                      !isADM ||
                      !form.id ||
                      funcionarioPermissoesBloqueado ||
                      salvandoPermissoes
                    }
                  >
                    {salvandoPermissoes ? "Salvando..." : "Salvar permissões"}
                  </Button>

                  <Button
                    variant="destructive"
                    onClick={revogarPermissoesAgenda}
                    disabled={!isADM || !form.id || salvandoPermissoes}
                  >
                    Revogar agenda
                  </Button>

                  <span className="perm-footer-hint">
                    As permissões são salvas na rota de permissões e não alteram o cadastro base do funcionário.
                  </span>
                </div>
              </>
            )}
          </section>
        )}

        {activeTab === "HOR" && (
          <section className="card section">
            <div className="section-title">Horários</div>

            {!form.id ? (
              <div>Salve o cadastro antes de configurar horários.</div>
            ) : (
              <>
                {loadingHor ? (
                  <div>Carregando...</div>
                ) : (
                  <div className="grid grid-3">
                    <div className="col-span-3">
                      <Label>Nome (exibição)</Label>
                      <Input
                        value={hor.NomeFunc}
                        onChange={(e) =>
                          setHor((h) => ({ ...h, NomeFunc: e.target.value }))
                        }
                        placeholder="Nome para exibição"
                      />
                    </div>

                    <div>
                      <Label>Entrada Manhã</Label>
                      <Input
                        value={hor.EntradaManha}
                        onChange={(e) =>
                          setHor((h) => ({
                            ...h,
                            EntradaManha: e.target.value,
                          }))
                        }
                        placeholder="08:00"
                      />
                    </div>

                    <div>
                      <Label>Saída Manhã</Label>
                      <Input
                        value={hor.SaidaManha}
                        onChange={(e) =>
                          setHor((h) => ({ ...h, SaidaManha: e.target.value }))
                        }
                        placeholder="12:00"
                      />
                    </div>

                    <div />

                    <div>
                      <Label>Entrada Tarde</Label>
                      <Input
                        value={hor.EntradaTarde}
                        onChange={(e) =>
                          setHor((h) => ({
                            ...h,
                            EntradaTarde: e.target.value,
                          }))
                        }
                        placeholder="13:00"
                      />
                    </div>

                    <div>
                      <Label>Saída Tarde</Label>
                      <Input
                        value={hor.SaidaTarde}
                        onChange={(e) =>
                          setHor((h) => ({ ...h, SaidaTarde: e.target.value }))
                        }
                        placeholder="17:00"
                      />
                    </div>

                    <div />

                    <div>
                      <Label>Tempo Almoço</Label>
                      <Input
                        value={hor.TempoAlmoco}
                        onChange={(e) =>
                          setHor((h) => ({ ...h, TempoAlmoco: e.target.value }))
                        }
                        placeholder="01:00"
                      />
                    </div>

                    <div>
                      <Label>Tolerância</Label>
                      <Input
                        value={hor.Tolerancia}
                        onChange={(e) =>
                          setHor((h) => ({ ...h, Tolerancia: e.target.value }))
                        }
                        placeholder="00:10"
                      />
                    </div>

                    <div />

                    <div
                      className="col-span-3"
                      style={{ display: "flex", gap: 8, marginTop: 8 }}
                    >
                      <Button onClick={salvarHorarios}>Salvar Horários</Button>

                      <Button
                        variant="outline"
                        onClick={() => carregarHorarios(form.id, form.nome)}
                      >
                        Recarregar
                      </Button>

                      <Button variant="destructive" onClick={removerHorarios}>
                        Remover
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>

      <InlineSetoresDrawer
        open={openSetores}
        onClose={() => setOpenSetores(false)}
        itens={setores}
        onRefresh={fetchSetores}
      />
    </MainLayout>
  );
}